import { Injectable } from '@angular/core';
import { map, Observable, throwError, forkJoin } from 'rxjs';

import { Page, queryPaginatedPost } from '../models/page';
import { AuthBaseService } from './auth-base.service';
import { UnpagedResult } from '../helper/dto/unpaged-result.dto';
import { UserDto } from '@/helper/dto/user.dto';
import { UserReadDto } from '@/helper/dto/user/read-user.dto';
import { BasicUserDto } from '@/helper/dto/user/basic-user.dto';
import { SettingDto } from '@/helper/dto/setting.dto';
import { FileUploadDto } from '@/helper/dto/file-upload.dto';
import { RoleDto } from '@/helper/dto/role.dto';
import { PermissionDto } from '@/helper/dto/permission.dto';
import { ProvisionListDto } from '../helper/dto/provision-list.dto';
import { ProvisionEntryDto } from '../helper/dto/provision-entry.dto';
import { ProvisionExportPayload } from '../helper/dto/provision-export.dto';
import { ProvisionAccessLogDto } from '../helper/dto/provision-access-log.dto';
import { AccountStatementDto } from '../helper/dto/account-statement.dto';
import { PartnerCompanyDto } from '@/helper/dto/partner-company.dto';
import { PartnerCompanyUserDto } from '@/helper/dto/partner-company-user.dto';
import { DocumentTypeDto } from '@/helper/dto/document-type.dto';
import { TableConfigDto, TableConfigUpdate } from "../helper/dto/table-config.dto";

import {
    PartnerCompanyDocumentFolderDto,
    UpdateManyPartnerCompanyDocumentVisibilityDto
} from '@/helper/dto/partner-company-document-folder.dto';
import { PartnerCompanyDocumentDto } from '@/helper/dto/partner-company-document.dto';
import { TypesenseSearchResult, TypesenseUserHit, TypesenseCustomerHit } from '@/helper/dto/typesense-search-result.dto';
import { SubordinatesResponseDto, AncestorsResponseDto, SearchSubordinatesResponseDto, DirectChildrenResponseDto, SubordinateCountResponseDto } from '@/helper/dto/subordinate-node.dto';

@Injectable({ providedIn: 'root' })
export class ApiService extends AuthBaseService {
    someApiCall(): Observable<any> {
        return this.http.get<any>(this.apiUrl('v1/some-api-call'), this.getOptions());
    }

    listSettingsUnpaged(): Observable<SettingDto[]> {
        return this.http.get<SettingDto[]>(this.apiUrl('v1/setting/unpaged'), this.getOptions());
    }

    deleteFile(fileId: string): Observable<FileUploadDto> {
        return this.http.delete<FileUploadDto>(this.apiUrl('v1/file-upload/' + fileId), this.getOptions());
    }

    getSetting(idOrKey: string): Observable<SettingDto> {
        return this.http.get<SettingDto>(this.apiUrl('v1/setting/' + idOrKey), this.getOptions());
    }

    uploadFile(file: File, isLogo: boolean = false): Observable<FileUploadDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<FileUploadDto>(this.apiUrl(`v1/file-upload?isLogo=${isLogo}`), formData, this.getOptions());
    }

    uploadFilePublic(file: File, isLogo: boolean = false): Observable<FileUploadDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<FileUploadDto>(this.apiUrl(`v1/file-upload/public?isLogo=${isLogo}`), formData, this.getOptions());
    }

    downloadFile(file: FileUploadDto, preview: boolean = false): Observable<any> {
        return file.isPublic ? this.serveFilePublic(file, preview) : this.serveFile(file._id!, preview);
    }

    resolveFilePath(file: FileUploadDto, preview: boolean = false): string {
        if (file.isPublic) {
            const path = (preview ? file.previewPath : file.path).replace(/^uploads-public[/\\]/, '');
            return this.apiUrl('v1/public-uploads/' + path.replaceAll('\\', '/'));
        }
        return this.apiUrl('v1/file-upload/serve/' + file._id + '?preview=' + preview);
    }

    serveFile(fileId: string, preview: boolean = false): Observable<any> {
        return this.http.get(this.apiUrl('v1/file-upload/serve/' + fileId + '?preview=' + preview), {
            responseType: 'arraybuffer',
            withCredentials: true
        });
    }

    serveFilePublic(file: FileUploadDto, preview: boolean = false): Observable<any> {
        const path = (preview ? file.previewPath : file.path).replace(/^uploads-public[/\\]/, '');
        return this.http.get(this.apiUrl('v1/public-uploads/' + path.replaceAll('\\', '/')), {
            responseType: 'arraybuffer'
        });
    }

    getMailRender(template: string, content: string): Observable<any> {
        return this.http.post(
            this.apiUrl(`v1/setting/generate-mail-preview`),
            { template, content },
            {
                withCredentials: true,
                responseType: 'arraybuffer'
            }
        );
    }

    createSetting(setting: SettingDto): Observable<SettingDto> {
        return this.http.post<SettingDto>(this.apiUrl('v1/setting'), setting, this.getOptions());
    }

    updateSetting(id: string, setting: SettingDto): Observable<SettingDto> {
        return this.http.patch<SettingDto>(this.apiUrl('v1/setting/' + id), setting, this.getOptions());
    }

    getAllUsersUnpaged(): Observable<UnpagedResult<UserDto>> {
        return this.http.get<UnpagedResult<UserDto>>(this.apiUrl('v1/user/'), this.getOptions());
    }

    searchUsers(urlOrFilter?: object): Observable<Page<UserDto>> {
        const uOb = queryPaginatedPost<UserDto>(this.http, this.apiUrl('v1/user/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<UserDto>(uOb);
        return page;
    }

    typesenseSearchUsers(query: string): Observable<Page<UserDto>> {
        const uOb = this.http.get(
            this.apiUrl(`v1/user/search?q=${encodeURIComponent(query)}`),
            this.getOptions()
        );
        return this.toPage<UserDto>(uOb);
    }

    typesenseSearchVPUsers(query: string): Observable<TypesenseSearchResult<TypesenseUserHit>> {
        return this.http.get<TypesenseSearchResult<TypesenseUserHit>>(
            this.apiUrl(`v1/user/search/vp?q=${encodeURIComponent(query)}`),
            this.getOptions()
        );
    }

    typesenseSearchCustomers(query: string): Observable<TypesenseSearchResult<TypesenseCustomerHit>> {
        return this.http.get<TypesenseSearchResult<TypesenseCustomerHit>>(
            this.apiUrl(`v1/customer/search?q=${encodeURIComponent(query)}`),
            this.getOptions()
        );
    }

    getUserById(userId: string): Observable<UserReadDto> {
        return this.http.get<UserReadDto>(this.apiUrl(`v1/user/${userId}`), this.getOptions());
    }

    createUser(userDto: UserDto): Observable<UserDto> {
        return this.http.post<UserDto>(this.apiUrl('v1/user'), userDto, this.getOptions());
    }

    updateUser(userId: string, userDto: UserDto): Observable<UserDto> {
        return this.http.patch<UserDto>(this.apiUrl(`v1/user/${userId}`), userDto, this.getOptions());
    }

    deleteUser(userId: string): Observable<UserDto> {
        return this.http.delete<UserDto>(this.apiUrl(`v1/user/${userId}`), this.getOptions());
    }

    getUserProfile(): Observable<UserReadDto> {
        return this.http.get<UserReadDto>(this.apiUrl('v1/user/my/profile'), this.getOptions());
    }

    updateUserProfile(userDto: UserDto): Observable<UserDto> {
        return this.http.patch<UserDto>(this.apiUrl('v1/user/my/profile'), userDto, this.getOptions());
    }

    getCurrentUser(): Observable<UserReadDto | BasicUserDto> {
        return this.http.get<UserReadDto | BasicUserDto>(this.apiUrl('v1/user/current/user'), this.getOptions());
    }

    updateCurrentUser(userDto: UserDto): Observable<UserDto> {
        return this.http.patch<UserDto>(this.apiUrl(`v1/current/user/`), userDto, this.getOptions());
    }

    // Role API methods
    searchRoles(urlOrFilter?: object): Observable<Page<RoleDto>> {
        const uOb = queryPaginatedPost<RoleDto>(this.http, this.apiUrl('v1/role/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<RoleDto>(uOb);
        return page;
    }

    getUserRoles(userId: string): Observable<RoleDto[]> {
        return this.http.get<RoleDto[]>(this.apiUrl(`v1/role/user/${userId}`), this.getOptions());
    }

    getCurrentUserRoles(): Observable<RoleDto[]> {
        return this.http.get<RoleDto[]>(this.apiUrl('v1/role/my'), this.getOptions());
    }

    getRole(roleId: string): Observable<RoleDto> {
        return this.http.get<RoleDto>(this.apiUrl(`v1/role/${roleId}`), this.getOptions());
    }

    createRole(roleDto: RoleDto): Observable<RoleDto> {
        return this.http.post<RoleDto>(this.apiUrl('v1/role'), roleDto, this.getOptions());
    }

    updateRole(roleId: string, roleDto: RoleDto): Observable<RoleDto> {
        return this.http.patch<RoleDto>(this.apiUrl(`v1/role/${roleId}`), roleDto, this.getOptions());
    }

    deleteRole(roleId: string): Observable<RoleDto> {
        return this.http.delete<RoleDto>(this.apiUrl(`v1/role/${roleId}`), this.getOptions());
    }

    cloneRole(roleId: string): Observable<RoleDto> {
        return this.http.patch<RoleDto>(this.apiUrl(`v1/role/${roleId}/clone`), {}, this.getOptions());
    }

    getRolesUnpaged(): Observable<RoleDto[]> {
        return this.http.post<RoleDto[]>(this.apiUrl('v1/role/unpaged'), {}, this.getOptions());
    }

    getRolesUnpagedPopulated(): Observable<RoleDto[]> {
        return this.http.post<RoleDto[]>(this.apiUrl('v1/role/unpaged/populated'), {}, this.getOptions());
    }

    getProvisionLists(cn: string, employeeNumber: string): Observable<ProvisionListDto[]> {
        return this.http.get<ProvisionListDto[]>(this.apiUrl(`v1/provision-calculation/${cn}/${employeeNumber}/list-provision-calculations`), this.getOptions());
    }

    getProvisionEntries(cn: string, employeeNumber: string, provisionListId: string): Observable<ProvisionEntryDto[]> {
        return this.http.get<ProvisionEntryDto[]>(this.apiUrl(`v1/provision-calculation/${cn}/${employeeNumber}/${provisionListId}/show-provision-calculation`), this.getOptions());
    }

    getAccountStatement(cn: string, employeeNumber: string, provisionListId: string): Observable<AccountStatementDto> {
        return this.http.get<AccountStatementDto>(this.apiUrl(`v1/provision-calculation/${cn}/${employeeNumber}/${provisionListId}/account-statement`), this.getOptions());
    }

    exportAccountStatementPdf(cn: string, employeeNumber: string, provisionListId: string): Observable<ArrayBuffer> {
        return this.http.get(this.apiUrl(`v1/provision-calculation/${cn}/${employeeNumber}/${provisionListId}/account-statement/pdf`), {
            withCredentials: true,
            responseType: 'arraybuffer'
        });
    }

    exportProvisionEntriesPdf(payload: ProvisionExportPayload): Observable<ArrayBuffer> {
        return this.http.post(this.apiUrl('v1/provision-calculation/export/pdf'), payload, {
            withCredentials: true,
            responseType: 'arraybuffer'
        });
    }

    exportProvisionEntriesXlsx(payload: ProvisionExportPayload): Observable<ArrayBuffer> {
        return this.http.post(this.apiUrl('v1/provision-calculation/export/xlsx'), payload, {
            withCredentials: true,
            responseType: 'arraybuffer'
        });
    }

    searchProvisionAccessLogs(urlOrFilter?: object): Observable<Page<ProvisionAccessLogDto>> {
        const uOb = queryPaginatedPost<ProvisionAccessLogDto>(this.http, this.apiUrl('v1/provision-access-log/search'), urlOrFilter, this.getOptions());
        return this.toPage<ProvisionAccessLogDto>(uOb);
    }

    // Permission API methods
    getPermissionsUnpaged(): Observable<PermissionDto[]> {
        return this.http.get<PermissionDto[]>(this.apiUrl('v1/permission/unpaged'), this.getOptions());
    }

    getMyPermissions(): Observable<PermissionDto[]> {
        return this.http.get<PermissionDto[]>(this.apiUrl('v1/permission/my'), this.getOptions());
    }

    getGroupedPermissions(): Observable<Record<string, PermissionDto[]>> {
        return this.http.get<Record<string, PermissionDto[]>>(this.apiUrl('v1/permission/grouped'), this.getOptions());
    }

    uploadFiles(files: File[], isPublic: boolean = false): Observable<any[]> {
        if (!files || files.length === 0)
            return new Observable((obs) => {
                obs.next([]);
                obs.complete();
            });

        const url = this.apiUrl(`v1/file-upload${isPublic ? '/public' : ''}`);

        const requests = files.map((f) => {
            const formData = new FormData();
            formData.append('file', f, f.name);
            return this.http.post<any>(url, formData, this.getOptions());
        });

        return forkJoin(requests);
    }

    toPage<T>(observable: any): Observable<Page<T>> {
        return observable.pipe(
            map((pageData: any) => {
                const page = new Page();
                if (pageData == null) {
                    return throwError(() => new Error('Page data is null'));
                }

                page.total = pageData.totalCount ? pageData.totalCount : 0;
                page.currentPage = pageData.page ? pageData.page : 1;
                page.pageSize = pageData.size ? pageData.size : 25;
                page.data = pageData.data ? (pageData.data as T[]) : [];
                page.duration = pageData.duration ? pageData.duration : 0;
                return page;
            })
        );
    }

    getSettingByKey(key: string): Observable<{ valueBoolean?: boolean; valueString?: string; valueNumber?: number }> {
        return this.http.get<{ valueBoolean?: boolean; valueString?: string; valueNumber?: number }>(this.apiUrl(`v1/setting/by-key/${key}`), this.getOptions());
    }

    getAllPartnerCompaniesUnpaged(): Observable<UnpagedResult<PartnerCompanyDto>> {
        return this.http.get<UnpagedResult<PartnerCompanyDto>>(this.apiUrl('v1/partner-company'), this.getOptions());
    }

    searchPartnerCompanies(urlOrFilter?: object): Observable<Page<PartnerCompanyDto>> {
        const uOb = queryPaginatedPost<PartnerCompanyDto>(this.http, this.apiUrl('v1/partner-company/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<PartnerCompanyDto>(uOb);
        return page;
    }

    getPartnerCompanyById(companyId: string): Observable<PartnerCompanyDto> {
        return this.http.get<PartnerCompanyDto>(this.apiUrl(`v1/partner-company/${companyId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    deletePartnerCompanyById(companyId: string): Observable<any> {
        return this.http.delete<any>(this.apiUrl(`v1/partner-company/${companyId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    createNewPartnerCompany(createPartnerCompanyDto: PartnerCompanyDto): Observable<any> {
        return this.http.post<any>(this.apiUrl('v1/partner-company'), createPartnerCompanyDto, this.getOptions());
    }

    updatePartnerCompany(companyId: string, partnerCompanyDto: Partial<PartnerCompanyDto>): Observable<PartnerCompanyDto> {
        return this.http.patch<PartnerCompanyDto>(this.apiUrl(`v1/partner-company/${companyId}`), partnerCompanyDto, this.getOptions());
    }

    getAllPartnerCompanyUsersUnpaged(): Observable<UnpagedResult<PartnerCompanyUserDto>> {
        return this.http.get<UnpagedResult<PartnerCompanyUserDto>>(this.apiUrl('v1/partner-company-user'), this.getOptions());
    }

    searchPartnerCompanyUsers(urlOrFilter?: object): Observable<Page<PartnerCompanyUserDto>> {
        const uOb = queryPaginatedPost<PartnerCompanyUserDto>(this.http, this.apiUrl('v1/partner-company-user/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<PartnerCompanyUserDto>(uOb);
        return page;
    }

    getPartnerCompanyUserById(userId: string): Observable<PartnerCompanyUserDto> {
        return this.http.get<PartnerCompanyUserDto>(this.apiUrl(`v1/partner-company-user/${userId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    deletePartnerCompanyUserById(userId: string): Observable<any> {
        return this.http.delete<any>(this.apiUrl(`v1/partner-company-user/${userId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    createNewPartnerCompanyUser(createPartnerCompanyUserDto: PartnerCompanyUserDto): Observable<any> {
        return this.http.post<any>(this.apiUrl('v1/partner-company-user'), createPartnerCompanyUserDto, this.getOptions());
    }

    updatePartnerCompanyUser(userId: string, partnerCompanyUserDto: Partial<PartnerCompanyUserDto>): Observable<PartnerCompanyUserDto> {
        return this.http.patch<PartnerCompanyUserDto>(this.apiUrl(`v1/partner-company-user/${userId}`), partnerCompanyUserDto, this.getOptions());
    }

    getFavouriteCompanies(): Observable<PartnerCompanyDto[]> {
        return this.http.get<PartnerCompanyDto[]>(this.apiUrl('v1/user/my/favourite-companies'), this.getOptions());
    }

    addFavouriteCompany(companyId: string): Observable<any> {
        return this.http.post<any>(this.apiUrl(`v1/user/my/favourite-companies/${companyId}`), {}, this.getOptions());
    }

    removeFavouriteCompany(companyId: string): Observable<any> {
        return this.http.delete<any>(this.apiUrl(`v1/user/my/favourite-companies/${companyId}`), this.getOptions());
    }

    getAllDocumentTypesUnpaged(): Observable<UnpagedResult<DocumentTypeDto>> {
        return this.http.get<UnpagedResult<DocumentTypeDto>>(this.apiUrl('v1/document-type'), this.getOptions());
    }

    searchDocumentTypes(urlOrFilter?: object): Observable<Page<DocumentTypeDto>> {
        const uOb = queryPaginatedPost<DocumentTypeDto>(this.http, this.apiUrl('v1/document-type/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<DocumentTypeDto>(uOb);
        return page;
    }

    getDocumentTypeById(typeId: string): Observable<DocumentTypeDto> {
        return this.http.get<DocumentTypeDto>(this.apiUrl(`v1/document-type/${typeId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    deleteDocumentTypeById(typeId: string): Observable<any> {
        return this.http.delete<any>(this.apiUrl(`v1/document-type/${typeId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    createNewDocumentType(createDocumentTypeDto: DocumentTypeDto): Observable<any> {
        return this.http.post<any>(this.apiUrl('v1/document-type'), createDocumentTypeDto, this.getOptions());
    }

    updateDocumentType(typeId: string, documentTypeDto: Partial<DocumentTypeDto>): Observable<DocumentTypeDto> {
        return this.http.patch<DocumentTypeDto>(this.apiUrl(`v1/document-type/${typeId}`), documentTypeDto, this.getOptions());
    }

    getAllPartnerCompanyDocumentFoldersUnpaged(): Observable<UnpagedResult<PartnerCompanyDocumentFolderDto>> {
        return this.http.get<UnpagedResult<DocumentTypeDto>>(this.apiUrl('v1/partner-company-document-folder'), this.getOptions());
    }

    searchPartnerCompanyDocumentFolders(urlOrFilter?: object): Observable<Page<PartnerCompanyDocumentFolderDto>> {
        const uOb = queryPaginatedPost<PartnerCompanyDocumentFolderDto>(this.http, this.apiUrl('v1/partner-company-document-folder/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<PartnerCompanyDocumentFolderDto>(uOb);
        return page;
    }

    getPartnerCompanyDocumentFolderById(folderId: string): Observable<PartnerCompanyDocumentFolderDto> {
        return this.http.get<PartnerCompanyDocumentFolderDto>(this.apiUrl(`v1/partner-company-document-folder/${folderId}`), this.getOptions());
    }

    getPartnerCompanyDocumentFoldersByPartnerCompany(partnerCompanyId: string): Observable<PartnerCompanyDocumentFolderDto[]> {
        return this.http.get<PartnerCompanyDocumentFolderDto[]>(this.apiUrl(`v1/partner-company-document-folder/by-partner-company/${partnerCompanyId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    deletePartnerCompanyDocumentFolderById(folderId: string): Observable<any> {
        return this.http.delete<any>(this.apiUrl(`v1/partner-company-document-folder/${folderId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    createNewPartnerCompanyDocumentFolder(createPartnerCompanyDocumentFolderDto: PartnerCompanyDocumentFolderDto): Observable<any> {
        return this.http.post<any>(this.apiUrl('v1/partner-company-document-folder'), createPartnerCompanyDocumentFolderDto, this.getOptions());
    }

    updatePartnerCompanyDocumentFolder(folderId: string, partnerCompanyDocumentFolderDto: Partial<PartnerCompanyDocumentFolderDto>): Observable<PartnerCompanyDocumentFolderDto> {
        return this.http.patch<PartnerCompanyDocumentFolderDto>(this.apiUrl(`v1/partner-company-document-folder/${folderId}`), partnerCompanyDocumentFolderDto, this.getOptions());
    }

    updatePartnerCompanyDocumentFoldersBulk(updates: { id: string; sortOrder?: number; parent?: string | null }[]): Observable<any> {
        return this.http.patch<any>(this.apiUrl('v1/partner-company-document-folder/bulk-update'), { updates }, this.getOptions());
    }

    updateManyPartnerCompanyDocumentFoldersVisibility(
        updateManyPartnerCompanyDocumentVisibilityDto: UpdateManyPartnerCompanyDocumentVisibilityDto
    ): Observable<any> {
        return this.http.patch<UpdateManyPartnerCompanyDocumentVisibilityDto>(
            this.apiUrl('v1/partner-company-document-folder/update-many-visibility'), updateManyPartnerCompanyDocumentVisibilityDto, this.getOptions()
        );
    }

    getAllPartnerCompanyDocumentsUnpaged(): Observable<UnpagedResult<PartnerCompanyDocumentDto>> {
        return this.http.get<UnpagedResult<PartnerCompanyDocumentDto>>(this.apiUrl('v1/partner-company-document'), this.getOptions());
    }

    searchPartnerCompanyDocuments(urlOrFilter?: object): Observable<Page<PartnerCompanyDocumentDto>> {
        const uOb = queryPaginatedPost<PartnerCompanyDocumentDto>(this.http, this.apiUrl('v1/partner-company-document/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<PartnerCompanyDocumentDto>(uOb);
        return page;
    }

    getPartnerCompanyDocumentById(documentId: string): Observable<PartnerCompanyDocumentDto> {
        return this.http.get<PartnerCompanyDocumentDto>(this.apiUrl(`v1/partner-company-document/${documentId}`), this.getOptions());
    }

    getPartnerCompanyDocumentsByPartnerCompany(partnerCompanyId: string): Observable<PartnerCompanyDocumentDto[]> {
        return this.http.get<PartnerCompanyDocumentDto[]>(this.apiUrl(`v1/partner-company-document/by-partner-company/${partnerCompanyId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    deletePartnerCompanyDocumentById(documentId: string): Observable<any> {
        return this.http.delete<any>(this.apiUrl(`v1/partner-company-document/${documentId}`), this.getOptions());
    }

    // TODO -> CORRECT TYPES
    createNewPartnerCompanyDocument(createPartnerCompanyDocumentDto: PartnerCompanyDocumentDto): Observable<any> {
        return this.http.post<any>(this.apiUrl('v1/partner-company-document'), createPartnerCompanyDocumentDto, this.getOptions());
    }

    updatePartnerCompanyDocument(documentId: string, partnerCompanyDocumentDto: Partial<PartnerCompanyDocumentDto>): Observable<PartnerCompanyDocumentDto> {
        return this.http.patch<PartnerCompanyDocumentDto>(this.apiUrl(`v1/partner-company-document/${documentId}`), partnerCompanyDocumentDto, this.getOptions());
    }

    updatePartnerCompanyDocumentsBulk(updates: { id: string; sortOrder?: number; parent?: string | null }[]): Observable<any> {
        return this.http.patch<any>(this.apiUrl('v1/partner-company-document/bulk-update'), { updates }, this.getOptions());
    }

    incrementPartnerCompanyDocumentDownloadCount(documentId: string): Observable<PartnerCompanyDocumentDto> {
        return this.http.patch<PartnerCompanyDocumentDto>(this.apiUrl(`v1/partner-company-document/${documentId}/download`), {}, this.getOptions());
    }

    getSubordinates(userId: string, depth?: number, excludeDeparted: boolean = false): Observable<SubordinatesResponseDto> {
        const params: string[] = [];
        if (depth !== undefined) params.push(`depth=${depth}`);
        if (excludeDeparted) params.push('excludeDeparted=true');
        const qs = params.length ? `?${params.join('&')}` : '';
        return this.http.get<SubordinatesResponseDto>(this.apiUrl(`v1/tree-path/${userId}/subordinates${qs}`), this.getOptions());
    }

    getDirectChildren(userId: string, excludeDeparted: boolean = false): Observable<DirectChildrenResponseDto> {
        const url = `v1/tree-path/${userId}/children${excludeDeparted ? '?excludeDeparted=true' : ''}`;
        return this.http.get<DirectChildrenResponseDto>(this.apiUrl(url), this.getOptions());
    }

    getSubordinateCount(userId: string, excludeDeparted: boolean = false): Observable<SubordinateCountResponseDto> {
        const url = `v1/tree-path/${userId}/count${excludeDeparted ? '?excludeDeparted=true' : ''}`;
        return this.http.get<SubordinateCountResponseDto>(this.apiUrl(url), this.getOptions());
    }

    getAncestors(userId: string): Observable<AncestorsResponseDto> {
        return this.http.get<AncestorsResponseDto>(this.apiUrl(`v1/tree-path/${userId}/ancestors`), this.getOptions());
    }

    searchSubordinates(
        userId: string,
        query: string
    ): Observable<SearchSubordinatesResponseDto> {
        return this.http.get<SearchSubordinatesResponseDto>(
            this.apiUrl(`v1/tree-path/${userId}/search`),
            {
                ...this.getOptions(),
                params: {
                    q: query
                }
            }
        );
    }

    getTableConfig(tableKey: string): Observable<TableConfigDto | null> {
        return this.http.get<TableConfigDto | null>(this.apiUrl(`v1/table-config/${tableKey}`), this.getOptions());
    }

    saveTableConfig(tableKey: string, update: TableConfigUpdate): Observable<TableConfigDto> {
        return this.http.put<TableConfigDto>(this.apiUrl(`v1/table-config/${tableKey}`), update, this.getOptions());
    }

    resetTableFilters(tableKey: string): Observable<TableConfigDto | null> {
        return this.http.delete<TableConfigDto | null>(this.apiUrl(`v1/table-config/${tableKey}/filters`), this.getOptions());
    }

    resetTableView(tableKey: string): Observable<TableConfigDto | null> {
        return this.http.delete<TableConfigDto | null>(this.apiUrl(`v1/table-config/${tableKey}/view`), this.getOptions());
    }

    // Career plan / cockpit API methods
    getCockpitStructure(): Observable<any> {
        return this.http.get<any>(this.apiUrl('v1/cockpit/structure'), this.getOptions());
    }

    getCockpitStructureChildren(parentId: number): Observable<any> {
        return this.http.get<any>(this.apiUrl(`v1/cockpit/structure/${parentId}`), this.getOptions());
    }

    getCareerPlan(vermittlerLfdNr: number, dateFetched?: string): Observable<any> {
        const endpoint = dateFetched
            ? `v1/cockpit/career-plan/${vermittlerLfdNr}?dateFetched=${encodeURIComponent(dateFetched)}`
            : `v1/cockpit/career-plan/${vermittlerLfdNr}`;
        return this.http.get<any>(this.apiUrl(endpoint), this.getOptions());
    }

    getCareerPlanDates(vermittlerLfdNr: number): Observable<any[]> {
        return this.http.get<any[]>(this.apiUrl(`v1/cockpit/career-plan/${vermittlerLfdNr}/dates`), this.getOptions());
    }

    updateCareerPlanPath(careerPlanPath: string): Observable<any> {
        return this.http.patch<any>(this.apiUrl('v1/user/my/career-plan-path'), { careerPlanPath }, this.getOptions());
    }

    syncCareerPlan(vermittlerLfdNr: number): Observable<any> {
        return this.http.post<any>(this.apiUrl(`v1/cockpit/career-plan/${vermittlerLfdNr}/sync`), {}, this.getOptions());
    }
}
